/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, LoggerService, UUID } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Types and Interfaces ===== */
import { EventData } from 'interfaces/eventData.interface';
import { EventDocument } from 'interfaces/document.interface';
import { Note } from 'interfaces/note.interface';

/* ===== Services ===== */
import { GlobalService } from './global.service';
import { EventWithoutEngagement } from 'interfaces/event.interface';
import { Event } from 'interfaces/types.interface';

interface RecurrentEventResponse {
  id: string;
  title?: string;
  themeId?: string;
  themeName?: string;
  authorId?: string;
  authorFullname?: string;
  createdDate?: string;
  updatedDate?: string | null;
  w5Who?: string;
  w5Where?: string;
  w5When?: string;
  w5What?: string;
  w5Why?: string;
  highlighted?: boolean;
  recurring?: boolean | number;
  [key: string]: string | number | boolean | undefined | null;
}

interface PaginatedEventsResponse {
  events: RecurrentEventResponse[];
  total: number;
  currentPage: number;
  totalPages: number;
}

interface PaginatedEventDataResponse {
  events: EventData[];
  total: number;
  currentPage: number;
  totalPages: number;
}

export class EventService {

  private static logger = new LoggerService( 'EventService' );

  /* ===== Send request to clear event data ===== */
  static cleanEventData( eventData: EventData ): EventWithoutEngagement {
    const eventActual: EventData = JSON.parse( JSON.stringify( eventData ) );

    delete eventActual.event.id;
    delete eventActual.event.createdDate;
    delete eventActual.event.updatedDate;
    delete eventActual.event.authorId;
    eventActual.event.highlighted = GlobalService.toBoolean( eventActual.event.highlighted || false );
    eventActual.event.recurring = GlobalService.toBoolean( eventActual.event.recurring || false );

    return eventActual.event;
  }

  /* ===== Send request to update event data ===== */
  static updateEventById(
    eventId: string, updatedEventData: EventData,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      const body = this.cleanEventData( updatedEventData );

      AXIOS.put<EventData>( `${API.EVENTS}/${eventId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch events by id ===== */
  static getEventById(
    eventId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventData> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTS}/${eventId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventData> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to create an event ===== */
  static createEvent(
    event: Event,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID | undefined> {
    return new Promise( async resolve => {
      const eventToSend = {
        title: event.title,
        themeId: event.themeId,
        recurring: event.recurring,
        highlighted: event.highlighted,
        referenceEventId: event.referenceEventId,
        w5What: event.w5What,
        w5When: event.w5When,
        w5Who: event.w5Who,
        w5Where: event.w5Where,
        w5Why: event.w5Why,
        engagements: event.engagements
      };

      AXIOS.post( `${API.EVENTS}`, eventToSend, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data.id );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to create a new note ===== */
  static createNote(
    eventId: string,
    noteData: {
      value: string;
      disable?: boolean;
      predecessorId?: string | null;
    },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Note> {
    const formattedNote = {
      value: noteData.value,
      disable: noteData.disable ?? false,
      predecessorId: noteData.predecessorId ?? null,
    };

    return new Promise( async resolve => {
      AXIOS.post( `${API.EVENTS}/${eventId}/notes`, formattedNote, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Note> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch all notes for an event by eventId and siteId ===== */
  static getNotesByEventId(
    eventId: string,
    siteId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Note[]> {
    if ( !siteId ) {
      return Promise.reject( new Error( 'siteId cannot be undefined' ) );
    }
    return new Promise( async resolve => {
      AXIOS.post(
        `${API.EVENTS}/${eventId}/notes/event`,
        { siteId },
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse<Note[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to update a note by noteId ===== */
  static updateNote(
    noteId: string,
    noteData: {
      value?: string;
      disable?: boolean;
    },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Note> {
    const formattedNote = {
      ...( noteData.value !== undefined && { value: noteData.value } ),
      ...( noteData.disable !== undefined && { disable: noteData.disable } ),
    };

    return new Promise( async resolve => {
      AXIOS.put(
        `${API.EVENTS}/${noteId}/notes`,
        formattedNote,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse<Note> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch all documents for an event by eventId ===== */
  static getDocumentsByEventId(
    eventId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventDocument[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTS}/${eventId}/documents`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventDocument[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch all documents for an event by eventId ===== */
  static postDocumentOnEvent(
    eventId: string, file: File,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      const formData = new FormData();
      formData.append( 'content', file );
      const tokenHeader = await CommonGlobalService.getHeaderRequest( token );
      const requestConfig = {
        headers: {
          ...tokenHeader.headers,
          'Content-Type': 'multipart/form-data'
        }
      };
      AXIOS.post( `${API.EVENTS}/${eventId}/documents`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch all documents for an event by eventId ===== */
  static deleteDocument(
    documentId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.EVENTS}/${documentId}/documents`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Update document rotation ===== */
  static updateDocumentRotation(
    documentId: UUID,
    rotation: number,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${API.EVENTS}/${documentId}/documents/rotation`, { rotation }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to download a document ===== */
  static downloadDocument(
    documentId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Blob> {
    return new Promise( async resolve => {
      const config = await CommonGlobalService.getHeaderRequest( token );
      config.responseType = 'blob';
      AXIOS.get( `${API.EVENTS}/documents/${ documentId }`, config )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const contentType = response.headers[ 'content-type' ] as string | undefined;
          const blob = new Blob( [ response.data ], { type: contentType } );
          resolve( blob );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch recurrent events by event manager ===== */
  static getRecurrentEventsByEventManager(
    eventManagerId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventData[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTS}/${eventManagerId}/recurrent-event`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<RecurrentEventResponse[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          let transformedEvents: EventData[] = [];

          if ( Array.isArray( response.data ) ) {
            transformedEvents = response.data
              .filter( eventWithEngagements => {
                if ( !eventWithEngagements || !eventWithEngagements.id ) {
                  return false;
                }
                return true;
              } )
              .map( eventWithEngagements => {
                const { engagements = [], ...eventProperties } = eventWithEngagements;

                const eventData = {
                  ...eventProperties,
                  title: eventProperties.title || '',
                  themeId: eventProperties.themeId || '',
                  highlighted: eventProperties.highlighted || false,
                  recurring: eventProperties.recurring || true,
                } as EventWithoutEngagement;

                return {
                  event: eventData,
                  engagements: engagements || [],
                  notes: [],
                  documents: []
                } as EventData;
              } );
          } else {
            console.warn( 'Expected array but received:', typeof response.data, response.data );
          }

          resolve( transformedEvents );
        } )
        .catch( ( err: AxiosError ) => {
          console.error( 'Error fetching recurrent events:', err );
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send request to fetch all events by event manager with pagination and search ===== */
  static getAllEventsByEventManager(
    eventManagerId: string,
    pagination: { page?: number; itemsPerPage?: number; searchFilter?: string },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<PaginatedEventDataResponse> {
    const { page = 1, itemsPerPage = 10, searchFilter = '' } = pagination || {};

    return new Promise( async resolve => {
      AXIOS.post(
        `${API.EVENTS}/${eventManagerId}/events`,
        { page, itemsPerPage, searchFilter },
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse<PaginatedEventsResponse> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          let transformedEvents: EventData[] = [];

          if ( response.data && Array.isArray( response.data.events ) ) {
            transformedEvents = response.data.events
              .filter( eventWithEngagements => {
                if ( !eventWithEngagements || !eventWithEngagements.id ) {
                  return false;
                }
                return true;
              } )
              .map( eventWithEngagements => {
                const { engagements = [], ...eventProperties } = eventWithEngagements;
                const eventData = {
                  ...eventProperties,
                  title: eventProperties.title || '',
                  themeId: eventProperties.themeId || '',
                  highlighted: eventProperties.highlighted || false,
                  recurring: Boolean( eventProperties.recurring ),
                  authorId: eventProperties.authorId,
                  createdDate: eventProperties.createdDate,
                  updatedDate: eventProperties.updatedDate,
                } as EventWithoutEngagement;

                return {
                  event: eventData,
                  engagements: engagements || [],
                  notes: [],
                  documents: [],
                  themeName: eventProperties.themeName,
                  authorFullname: eventProperties.authorFullname,
                  createdDate: eventProperties.createdDate,
                } as EventData & { themeName?: string; authorFullname?: string; createdDate?: string };
              } );
          } else {
            console.warn( 'Expected object with events property but received:', typeof response.data, response.data );
          }

          const result = {
            events: transformedEvents,
            total: response.data?.total || 0,
            currentPage: response.data?.currentPage || page,
            totalPages: response.data?.totalPages || 0
          };
          resolve( result );
        } )
        .catch( ( err: AxiosError ) => {
          console.error( 'Error fetching events:', err );
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Service to create event, then notes and documents ===== */
  static async createEventWithNotesAndDocuments(
    event: Event,
    notes: Array<{ value: string; disable?: boolean; predecessorId?: string | null }>,
    documents: File[],
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<UUID | undefined> {
    try {
      EventService.logger.verbose( 'Creating event' );
      EventService.logger.verbose( 'Event data:', event );
      EventService.logger.verbose( 'Notes data:', notes );
      const eventId = await this.createEvent( event, token, dispatch, onError, onWarning );

      if ( !eventId ) {
        console.error( 'Échec de création de l\'événement: pas d\'ID retourné' );
        return undefined;
      }

      if ( documents && documents.length > 0 ) {

        await Promise.all(
          documents.map( async file => {
            try {
              return await this.postDocumentOnEvent( eventId, file, token, dispatch, onError, onWarning );
            } catch ( docError ) {
              console.error( 'Erreur lors de l\'envoi d\'un document:', docError );
            }
          } )
        );
      }

      if ( notes && notes.length > 0 ) {

        for ( const noteData of notes ) {
          try {
            await this.createNote( eventId, noteData, token, dispatch, onError, onWarning );
            await new Promise( timeoutResolve => { setTimeout( timeoutResolve, 200 ); } );
          } catch ( noteError ) {
            console.error( 'Erreur lors de la création d\'une note:', noteError );
          }
        }

        await new Promise( timeoutResolve => { setTimeout( timeoutResolve, 500 ); } );
      }

      return eventId;
    } catch ( err ) {
      GlobalService.onAxiosError( err as AxiosError, dispatch, onError );
      return undefined;
    }
  }

  /* ===== Send request to download event ticket PDF ===== */
  static downloadTicketPDF(
    eventId: string,
    siteId?: string,
    language?: string,
    token: string | null = null,
    dispatch?: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<Blob> {
    return new Promise( async resolve => {
      try {
        const config = await CommonGlobalService.getHeaderRequest( token );
        config.responseType = 'blob';

        let url = `${API.EVENTS}/${eventId}/pdf`;
        if ( siteId ) {
          url += `/${encodeURIComponent( siteId )}`;
          if ( language ) {
            url += `/${encodeURIComponent( language )}`;
          }
        } else if ( language ) {
          url += `?locale=${encodeURIComponent( language )}`;
        }

        AXIOS.get( url, config )
          .then( ( response: AxiosResponse ) => {
            if ( dispatch ) {
              CommonGlobalService.saveRequestToken( response, dispatch );
            }
            if ( response.status === 202 ) {
              GlobalService.onAxiosWarning( response, onWarning );
            }
            const contentType = response.headers[ 'content-type' ];
            const mimeType = typeof contentType === 'string' ? contentType : 'application/pdf';
            const blob = new Blob( [ response.data ], { type: mimeType } );
            resolve( blob );
          } )
          .catch( ( err: AxiosError ) => {
            if ( dispatch ) {
              GlobalService.onAxiosError( err, dispatch, onError );
            }
            console.error( 'Erreur lors du téléchargement du PDF:', err );
          } );
      } catch ( err ) {
        console.error( 'Erreur lors de la préparation du téléchargement du PDF:', err );
      }
    } );
  }

}
